import React, { useState, useTransition } from "react";
import { PlayerBirthdaysListResponseDto } from "@/lib/api/interfaces/players.interface";
import { usePlayersFindBirthdaysQuery } from "@/lib/api/requests/players.requests";
import { useTeamsFindQuery } from "@/lib/api/requests/teams.requests";
import { formatFriendlyBirthday } from "@/lib/utils/date-formatter";
import { Button } from "@/ui-components/ui/button";
import { Skeleton } from "@/ui-components/ui/skeleton";
import { Cake, Gift } from "lucide-react";
import { DefaultTemplateType } from "@/page-components/post-generator/template-editor/registry/defaultTemplates";
import { MissingTemplateDialog } from "../../../util-components/dialog/MissingTemplateDialog";
import PlayerDetailsDialog from "@/page-components/players/details-dialog";
import { useDefaultTemplate } from "@/lib/hooks/use-default-template";

/**
 * Widget to display upcoming player birthdays.
 * 
 * Shows a list of players with upcoming birthdays.
 * Allows quick action to create a birthday post using the default template.
 */
const UpcomingBirthdays = () => {
    const { data: players, isLoading } = usePlayersFindBirthdaysQuery({ limit: 4 });
    const { data: teams, isLoading: isLoadingTeams } = useTeamsFindQuery();

    if (isLoading || isLoadingTeams || !players) {
        return <BirthdaysSkeleton />
    }

  return (
    <div className="divide-y divide-gray-100">
        {players?.docs?.map((player) => (
            <UpcomingBirthday key={player._id} player={player} teams={teams?.docs || []} />
        ))}
        {(!players?.docs || players?.docs?.length === 0) && (
          <div className="p-8 text-center">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-gray-50 mb-3 text-gray-400">
                 <Gift className="w-6 h-6" />
            </div>
            <p className="text-sm font-medium text-gray-900">Keine bevorstehenden Geburtstage</p>
            <p className="text-xs text-gray-500 mt-1">Die nächsten Geburtstage erscheinen hier.</p>
          </div>
        )}
    </div>
  );
};

const UpcomingBirthday = ({ player, teams }: { player: PlayerBirthdaysListResponseDto, teams: any[] }) => {
    const playerTeam = teams.find((team) => team._id === player.belongsToTeamId);
    
    const { handleCreatePost, isLoading: isLoadingDefaults, missingTemplateDialogProps } = useDefaultTemplate({
        prefillData: {
            entityType: 'Player',
            entityId: player._id
        }
    });

    const [detailsOpen, setDetailsOpen] = useState(false);
    const [isPending, startTransition] = useTransition();

    const imageUrl = player.thumbnailUrl || player.imageUrl;

    const handleOpenDetails = () => {
        startTransition(() => {
            setDetailsOpen(true);
        });
    };

    const handleClick = (e: React.MouseEvent) => {
        handleCreatePost(DefaultTemplateType.BIRTHDAY, "Geburtstags-Posts", e);
    };
  
  return (
    <>
        <div 
            onClick={handleOpenDetails}
            className={`group relative flex flex-col sm:flex-row sm:items-center justify-between p-4 hover:bg-gray-50 transition-colors gap-4 cursor-pointer ${isPending ? "opacity-50 pointer-events-none" : ""}`}
        >
            <div className="flex items-center gap-4 min-w-0">
                <div className="h-10 w-10 shrink-0 rounded-full bg-pink-100/50 text-pink-600 flex items-center justify-center font-bold text-sm overflow-hidden border border-pink-100">
                    {imageUrl ? (
                        <img src={imageUrl} alt={`${player.firstName} ${player.lastName}`} className="w-full h-full object-cover object-top" />
                    ) : (
                        player.firstName.charAt(0)
                    )}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-semibold text-gray-900 truncate">{player.firstName} {player.lastName}</span>
                        {player.nickname && (
                            <span className="text-[10px] text-gray-500 px-1.5 py-0.5 bg-gray-100 rounded-md">"{player.nickname}"</span>
                        )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>{formatFriendlyBirthday(player.birthDate)}</span>
                        {playerTeam && (
                            <>
                                <span>•</span>
                                <span className="truncate">{playerTeam.title}</span>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {player.birthDateConsent && (
            <div className="flex items-center gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity sm:absolute sm:right-0 sm:top-0 sm:bottom-0 sm:pr-4 sm:pl-24 sm:bg-gradient-to-r sm:from-transparent sm:via-gray-50 sm:to-gray-50">
                <Button
                    size="sm"
                    variant="outline"
                    disabled={isLoadingDefaults}
                    onClick={handleClick}
                    className="relative z-10 h-10 text-xs bg-white text-pink-600 border-pink-100 hover:bg-pink-50 hover:text-pink-700 hover:border-pink-200 disabled:opacity-60 px-4 rounded-xl shadow-sm"
                >
                    <Cake className="w-3.5 h-3.5 mr-1.5" />
                    Gratulieren
                </Button>
            </div>
            )}
        </div>
        <MissingTemplateDialog
            {...missingTemplateDialogProps}
            description="Wähle ein Standard-Template für Geburtstage aus. Danach können Sie direkt aus dem Dashboard heraus gratulieren."
        />
        {detailsOpen && (
            <PlayerDetailsDialog 
                open={detailsOpen}
                onClose={() => setDetailsOpen(false)}
                playerId={player._id}
            />
        )}
    </>
  );
};

const BirthdaysSkeleton = () => {
    return (
        <div className="divide-y divide-gray-100">
            {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-4 p-4">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-24" />
                    </div>
                    <div>
                        <Skeleton className="h-8 w-24" />
                    </div>
                </div>
            ))}
        </div>
    )
}

export default UpcomingBirthdays;
